import cn from 'classnames';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  ReportRequest_InputType_CBCT_IOS_Superimposition,
  ReportRequest_InputType_StudioImplant,
  ReportRequest_InputType_StudioOrtho,
} from '@/shared/api/protocol-ts/model/dto_report_request_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { getStudyIDsByType } from '@/shared/lib';
import { Button, Modal } from '@/shared/ui';

import { useDiagnocoinFundingByStudyType } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';
import { StudyInfo } from '@/entities/study';

import { useNewReport } from '@/features/addNewReport';
import { useRemoveStudy } from '@/features/removeStudy';

import { isSecondaryStudyMetaInfo, isStudyMetaInfoOfType } from '../../lib';
import { selectors } from '../../model';
import { ReportInfoRow } from '../ReportInfoRow/ReportInfoRow';
import { ReportsVersions } from '../ReportsVersions/ReportVersions';

import styles from './ReportInfoModal.module.scss';

export const ReportInfoModal = () => {
  const [downloading, setDownloading] = useState(false);

  const { patientID = '' } = useParams();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const {
    visible,
    data: {
      sourceStudyIDs,
      reportType,
      orientation,
      shouldHideDownload,
      hideMode,
      isHidden,
      removeReport,
      restoreReport,
      downloadReport,
    },
  } = useAppSelector(modalModel.selectors.selectReportInfoModal);
  const { studiesMetaInfo, reportsInfo } = useAppSelector(
    selectors.selectReportInfo(sourceStudyIDs, reportType, orientation),
  );
  const { CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const {
    requestIOXRayGPReport,
    requestPanoGPReport,
    requestCBCTGPReport,
    requestCBCTSegmentationReport,
    requestStudioOrthoReport,
    requestStudioImplantReport,
    requestCBCTIOSSuperimpositionReport,
    requestCBCTOrthoReport,
    requestIOSSegmentationReport,
  } = useNewReport();
  const removeStudy = useRemoveStudy();
  const reorderFunding = useDiagnocoinFundingByStudyType(
    studiesMetaInfo[0]?.studyType,
  );

  const reorderLabel =
    reorderFunding.source === 'diagnocoins' ? (
      <FormattedMessage
        id="global.regenerateForCoins"
        defaultMessage="Regenerate for {coins, plural, one {# coin} other {# coins}}"
        values={{ coins: reorderFunding.coins }}
      />
    ) : (
      <FormattedMessage id="global.regenerate" defaultMessage="Regenerate" />
    );

  const closeModal = () => {
    dispatch(modalModel.actions.closeModal(ModalID.ReportInfo));
  };

  // NOTE: When the last report is deleted, the ReportInfoModal should close
  const closeModalAndRemoveStudyHandler = async () => {
    if (reportsInfo.length === 1) {
      closeModal();

      // NOTE: A temporary solution until ReportType_IOS_Segmentation is fully implemented. The ReportType for all reportsInfo is the same
      if (
        reportsInfo[0].reportType === ReportType.ReportType_IOS_Segmentation
      ) {
        await removeStudy(studiesMetaInfo[0].ID);
      }
    }
  };

  // TODO: [8|m] Make reusable hook for reordering reports
  const reorderReport = () => {
    const CBCTStudyID = studiesMetaInfo.find(
      isStudyMetaInfoOfType(StudyType.StudyType_CBCT),
    )?.ID;

    switch (reportType) {
      case ReportType.ReportType_CBCT_GP: {
        if (CBCTStudyID) {
          requestCBCTGPReport(CBCTStudyID);
        }
        break;
      }
      case ReportType.ReportType_Pano_GP: {
        const studyId = studiesMetaInfo.find(
          isStudyMetaInfoOfType(StudyType.StudyType_PanoramicXRay),
        )?.ID;

        if (studyId) {
          requestPanoGPReport(studyId);
        }
        break;
      }
      case ReportType.ReportType_IOXRay_GP: {
        const studyId = studiesMetaInfo.find(
          isStudyMetaInfoOfType(StudyType.StudyType_IntraoralXRay),
        )?.ID;

        if (studyId) {
          requestIOXRayGPReport(studyId);
        }
        break;
      }
      case ReportType.ReportType_IOS_Segmentation: {
        const IOSMeshesStudyID = studiesMetaInfo.find(
          isStudyMetaInfoOfType(StudyType.StudyType_IOS_Meshes),
        )?.ID;

        if (IOSMeshesStudyID) {
          requestIOSSegmentationReport(IOSMeshesStudyID);
        }
        break;
      }
      case ReportType.ReportType_CBCT_IOS_Superimposition: {
        const IOSMeshesStudyID = studiesMetaInfo.find(
          isStudyMetaInfoOfType(StudyType.StudyType_IOS_Meshes),
        )?.ID;

        if (CBCTStudyID) {
          requestCBCTIOSSuperimpositionReport(
            new ReportRequest_InputType_CBCT_IOS_Superimposition({
              CBCTStudyID,
              STLStudyID: IOSMeshesStudyID,
              DentalPhotoStudyIDs: getStudyIDsByType(
                studiesMetaInfo,
                StudyType.StudyType_DentalPhoto,
              ),
              Orientation: orientation,
            }),
          );
        }
        break;
      }
      case ReportType.ReportType_CBCT_OrthoSlides: {
        if (CBCTStudyID) {
          requestCBCTOrthoReport(CBCTStudyID);
        }

        break;
      }
      case ReportType.ReportType_StudioOrtho: {
        const IOSMeshesStudyID = studiesMetaInfo.find(
          isStudyMetaInfoOfType(StudyType.StudyType_IOS_Meshes),
        )?.ID;

        if (CBCTStudyID) {
          const secondaryCBCTStudyID = studiesMetaInfo.find(
            isSecondaryStudyMetaInfo(CBCTStudyID, StudyType.StudyType_CBCT),
          )?.ID;

          requestStudioOrthoReport(
            new ReportRequest_InputType_StudioOrtho({
              CBCTStudyID,
              IOSMeshesStudyID,
              DentalPhotoStudyIDs: getStudyIDsByType(
                studiesMetaInfo,
                StudyType.StudyType_DentalPhoto,
              ),
              SecondaryCBCTStudyID: secondaryCBCTStudyID,
            }),
          );
        }
        break;
      }
      case ReportType.ReportType_StudioImplant: {
        const IOSMeshesStudyID = studiesMetaInfo.find(
          isStudyMetaInfoOfType(StudyType.StudyType_IOS_Meshes),
        )?.ID;

        if (CBCTStudyID) {
          requestStudioImplantReport(
            new ReportRequest_InputType_StudioImplant({
              CBCTStudyID,
              IOSMeshesStudyID,
              DentalPhotoStudyIDs: getStudyIDsByType(
                studiesMetaInfo,
                StudyType.StudyType_DentalPhoto,
              ),
            }),
          );
        }
        break;
      }

      case ReportType.ReportType_CBCT_Segmentation:
        {
          if (CBCTStudyID) {
            requestCBCTSegmentationReport(CBCTStudyID);
          }
        }
        break;
    }
    closeModal();
  };

  const onDownloadReport = async () => {
    setDownloading(true);
    await downloadReport?.();
    setDownloading(false);
  };

  const onRestoreReport = async () => {
    closeModal();
    await restoreReport?.();
  };

  // A hidden report is not renderable or regenerable: the actions left are
  // Restore and permanent Delete, offered on mobile to users allowed to do so.
  const showFooter = isHidden
    ? isMobile && Boolean(restoreReport || removeReport)
    : reportType !== ReportType.ReportType_Pano_Bitewings && CanRequestReport;

  return (
    <Modal
      data-component="report-info-modal"
      headerClassName={styles.header}
      bodyClassName={styles.body}
      footerClassName={styles.footer}
      isOpen={visible}
      onCancel={closeModal}
      title={formatMessage({
        id: 'reportInformationModal.title',
        defaultMessage: 'Report Information',
      })}
      hideFooter={!showFooter}
      borderless={isMobile}
      footer={
        isMobile && isHidden ? (
          <>
            {removeReport && (
              <Button danger variant="tertiary" onClick={removeReport}>
                <FormattedMessage id="global.delete" defaultMessage="Delete" />
              </Button>
            )}
            {restoreReport && (
              <Button onClick={onRestoreReport} icon="back">
                <FormattedMessage
                  id="reportCard.restore"
                  defaultMessage="Restore"
                />
              </Button>
            )}
          </>
        ) : isMobile ? (
          <>
            <Button
              danger
              icon={hideMode ? 'archive' : 'delete'}
              variant="tertiary"
              onClick={removeReport}
            >
              {hideMode ? (
                <FormattedMessage
                  id="global.archive"
                  defaultMessage="Archive"
                />
              ) : (
                <FormattedMessage id="global.delete" defaultMessage="Delete" />
              )}
            </Button>
            {!shouldHideDownload && (
              <Button onClick={onDownloadReport} loading={downloading}>
                <FormattedMessage
                  id="global.downloadReport"
                  defaultMessage="Download report"
                />
              </Button>
            )}
          </>
        ) : (
          <Button size="tiny" variant="secondary" onClick={reorderReport}>
            {reorderLabel}
          </Button>
        )
      }
    >
      <main className={cn(styles.content)}>
        {studiesMetaInfo.map((studyMetaInfo) => (
          <StudyInfo
            key={studyMetaInfo.ID}
            {...studyMetaInfo}
            showViewIntraoralScanButton={
              studyMetaInfo.studyType === StudyType.StudyType_IOS_Meshes
            }
          />
        ))}
        {!isHidden && (
          <ReportsVersions>
            {reportsInfo.map((reportInfo) => (
              <ReportInfoRow
                key={reportInfo.reportID}
                onRemoveReport={closeModalAndRemoveStudyHandler}
                {...reportInfo}
              />
            ))}
          </ReportsVersions>
        )}
        {isMobile && !isHidden && (
          <Button block variant="secondary" onClick={reorderReport}>
            {reorderLabel}
          </Button>
        )}
      </main>
    </Modal>
  );
};
